import { createRouter, createWebHistory } from 'vue-router'
import { dayKey } from './lib/calendar'

// createWebHistory (not hash history) relies on the Worker serving index.html
// for unknown paths — that is what `not_found_handling =
// "single-page-application"` in wrangler.toml does.
//
// A link opens as a child of the library, so the list (and its scroll
// position) stays on screen beside it; on narrow screens HomeView shows one
// pane at a time instead. The calendar does the same with its own child.
export const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      component: () => import('./views/HomeView.vue'),
      children: [
        { path: '', name: 'home', component: () => import('./views/EmptyPane.vue') },
        {
          path: 'links/:id',
          name: 'link',
          component: () => import('./views/LinkView.vue'),
          props: true
        }
      ]
    },
    // The calendar opens on this week — or, on a phone, today.
    {
      path: '/calendar',
      redirect: () => {
        const narrow = typeof matchMedia === 'function' && matchMedia('(max-width: 820px)').matches
        return `/calendar/${narrow ? 'day' : 'week'}/${dayKey(new Date())}`
      }
    },
    {
      path: '/calendar/:view(month|week|day)/:date(\\d{4}-\\d{2}-\\d{2})',
      name: 'calendar',
      component: () => import('./views/CalendarView.vue'),
      children: [
        {
          path: 'links/:id',
          name: 'calendar-link',
          component: () => import('./views/LinkView.vue'),
          props: true
        }
      ]
    },
    // Like the library: the list, and the open item with its source beside it.
    // Links opened from there stay beside the ledger, as the calendar's do.
    {
      path: '/ledger',
      component: () => import('./views/LedgerView.vue'),
      children: [
        { path: '', name: 'ledger', component: () => import('./views/LedgerEmpty.vue') },
        {
          path: 'items/:itemId',
          name: 'ledger-item',
          component: () => import('./views/LedgerItemPane.vue'),
          props: true
        },
        // A link reached from an item (closed by, the meeting's other notes).
        {
          path: 'links/:id',
          name: 'ledger-link',
          component: () => import('./views/LinkView.vue'),
          props: true
        }
      ]
    },
    // A brief's own URL (db/brief-store.ts) opens the link it is stored as.
    {
      path: '/briefs/:eventId',
      name: 'brief',
      component: () => import('./views/BriefRedirect.vue'),
      props: true
    },
    // A meeting's debrief: the page that writes it, and the stored notes' own URL (db/debriefs.ts).
    {
      path: '/debriefs/:eventId',
      name: 'debrief',
      component: () => import('./views/DebriefView.vue'),
      props: true
    },
    // Like the ledger: the review queue or everyone, and the open pair or
    // person beside it. Links opened from a person stay beside the list.
    {
      path: '/people',
      component: () => import('./views/PeopleView.vue'),
      children: [
        { path: '', name: 'people', component: () => import('./views/PeopleEmpty.vue') },
        {
          path: 'review/:keep/:fold',
          name: 'people-pair',
          component: () => import('./views/PersonPairPane.vue'),
          props: true
        },
        {
          path: 'links/:id',
          name: 'people-link',
          component: () => import('./views/LinkView.vue'),
          props: true
        },
        // My org (view=org): a forum, and the roles.
        {
          path: 'forums/:forumId',
          name: 'forum',
          component: () => import('./views/ForumPane.vue'),
          props: true
        },
        { path: 'org/roles', name: 'org-roles', component: () => import('./views/RolesPane.vue') },
        {
          path: ':id',
          name: 'person',
          component: () => import('./views/PersonPane.vue'),
          props: true
        }
      ]
    },
    { path: '/sync', name: 'sync', component: () => import('./views/SyncView.vue') },
    { path: '/devices', name: 'devices', component: () => import('./views/DevicesView.vue') }
  ]
})
